import { authService } from "@/services/auth";
import { registerUser } from "@/api/user/user";
import { useState, useEffect, useCallback } from "react";

import { paths } from "../../routes/paths";
import { useAuthContext } from "../../hooks/use-auth-context";
import { SplashScreen } from "../../components/loading-screen";
import { useRouter, usePathname, useSearchParams } from "../../routes/hooks";

type Props = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: Props) {
  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();
  const { user, loading, authenticated, mfaEnabled } = useAuthContext();
  const [isChecking, setIsChecking] = useState(true);
  const [isMfaVerified, setIsMfaVerified] = useState(false);
  const [checkingMfa, setCheckingMfa] = useState(false);

  // Helper to create query strings for redirects
  const createQueryString = useCallback(
    (name: string, value: string) => {
      if (!params) return "";
      const newParams = new URLSearchParams(params.toString());
      newParams.set(name, value);
      return newParams.toString();
    },
    [params]
  );

  // Session storage check for registration completion
  const getHasRegistered = useCallback(() => {
    return sessionStorage.getItem(`user_registered_${user?.id}`) === "true";
  }, [user?.id]);

  const setHasRegistered = useCallback(() => {
    if (user?.id) {
      sessionStorage.setItem(`user_registered_${user.id}`, "true");
    }
  }, [user?.id]);

  // Check if MFA has been verified for this session
  const getMfaVerifiedForSession = useCallback(() => {
    if (!user?.id) return false;
    return (
      sessionStorage.getItem(
        `mfa_verified_${user.id}_${new Date().toDateString()}`
      ) === "true"
    );
  }, [user?.id]);

  const setMfaVerifiedForSession = useCallback(() => {
    if (user?.id) {
      // Store MFA verification with date to require verification once per day
      sessionStorage.setItem(
        `mfa_verified_${user.id}_${new Date().toDateString()}`,
        "true"
      );
      setIsMfaVerified(true);
    }
  }, [user?.id]);

  // Check if the user needs to verify MFA
  const checkMfaRequirements = useCallback(async () => {
    if (!authenticated || loading || !user?.id || !mfaEnabled || checkingMfa)
      return;

    // Skip MFA redirection if we're already on the verification page
    if (pathname.includes("/auth/confirm-registration")) {
      return;
    }

    setCheckingMfa(true);
    try {
      // Check if MFA has been verified for this session
      const isVerifiedForSession = getMfaVerifiedForSession();
      if (isVerifiedForSession) {
        setIsMfaVerified(true);
        setCheckingMfa(false);
        return;
      }

      // Redirect to MFA verification
      const returnTo = pathname.startsWith("/auth")
        ? paths.dashboard.root()
        : pathname;
      router.replace(
        `/auth/confirm-registration?returnTo=${encodeURIComponent(returnTo)}`
      );
    } catch (error) {
      console.error("Error checking MFA status:", error);
    } finally {
      setCheckingMfa(false);
    }
  }, [
    authenticated,
    loading,
    user?.id,
    mfaEnabled,
    checkingMfa,
    pathname,
    getMfaVerifiedForSession,
    router,
  ]);

  // Check for authentication and redirect to login if not authenticated
  const checkAuthentication = useCallback(async () => {
    if (loading) return;

    if (!authenticated) {
      const signInPath = paths.auth.login;
      const returnTo = pathname;
      const queryString = createQueryString("returnTo", returnTo);
      const href = queryString ? `${signInPath}?${queryString}` : signInPath;
      router.replace(href);
      return;
    }

    // If authenticated, check if MFA verification is needed
    if (authenticated && mfaEnabled) {
      await checkMfaRequirements();
    }
  }, [
    authenticated,
    loading,
    router,
    pathname,
    createQueryString,
    mfaEnabled,
    checkMfaRequirements,
  ]);

  // Handle Google user registration (if needed)
  const registerGoogleUser = useCallback(async () => {
    if (
      !loading &&
      authenticated &&
      user?.app_metadata?.provider === "google" &&
      !getHasRegistered()
    ) {
      try {
        const fullName = user.user_metadata?.full_name || "";
        const [firstName, ...lastNameParts] = fullName.split(" ");
        const lastName = lastNameParts.join(" ");

        const response = await registerUser({
          email: user.email ?? "",
          id: user.id,
          firstName,
          lastName,
          profilePictureUrl: user.user_metadata?.avatar_url,
        });

        if (response.ok) {
          setHasRegistered();
          if (response.data?.workspace) {
            router.push(paths.dashboard.root());
          }
        }
      } catch (error) {
        console.error("Error during user registration:", error);
      }
    }
    setIsChecking(false);
  }, [
    authenticated,
    loading,
    user,
    router,
    getHasRegistered,
    setHasRegistered,
  ]);

  // Effect to check authentication status
  useEffect(() => {
    checkAuthentication();
  }, [checkAuthentication]);

  // Effect to handle Google user registration
  useEffect(() => {
    registerGoogleUser();
  }, [registerGoogleUser]);

  // Export MFA verification status to window for use in the confirmation page
  useEffect(() => {
    if (window) {
      (window as any).setMfaVerifiedForSession = setMfaVerifiedForSession;
    }

    // Check if MFA is already verified for this session on mount
    if (user?.id && mfaEnabled) {
      const isVerified = getMfaVerifiedForSession();
      setIsMfaVerified(isVerified);
    }
  }, [
    user?.id,
    mfaEnabled,
    getMfaVerifiedForSession,
    setMfaVerifiedForSession,
  ]);

  if (loading || isChecking || (mfaEnabled && !isMfaVerified && checkingMfa)) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}

export default AuthGuard;
